import * as analytics from 'analytics/Analytics';
import {ContextMenuDisplayGroup, ClickableContextMenuOption} from 'components/ContextMenu';
import {ActionMethod, CommonAnalyticsActions} from 'constants/AnalyticsConstants';
import documentStore from 'notebook/documents/DocumentStore';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import allowTableMappingInDocument from 'notebook/structured-tables/structured-table-mappings/allowTableMappingInDocument';
import StructuredTableMappingConfigModal from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConfigModal';
import {
  trackStructuredTableMappingConfigModalEvent,
  trackUpdateStructuredTableMappingsEvent,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {
  StructuredTableMappingsState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {
  ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
  REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
} from 'notebook/tables/core-ui/TableAxisCell';
import * as TableConstants from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import legacyRunModal from 'ui-library/legacyRunModal';
import APIUtil from 'util/APIUtil';

export const ADD_TABLE_MAPPING_NAME = 'Add table mapping';
export const EDIT_TABLE_MAPPING_NAME = 'Edit table mapping';
export const REMOVE_TABLE_MAPPING_NAME = 'Remove table mapping';

const TOOLTIP_DISABLED_READ_ONLY = 'Cannot map into read-only columns';
const TOOLTIP_DISABLED_ARCHIVED = 'Cannot map into archived columns';
const TOOLTIP_DISABLED_OTHER_COLUMN_MAPPED = 'Cannot map more than one column on the same table';

/**
 * Returns the note from the appropriate store (entry or template).
 * As a sanity check, returns fallbackNote if the fileId is not in the store.
 */
function getNoteFromStore(fileId: string, fallbackNote: Note): Note {
  let note: Note | null = null;
  if (APIUtil.isEntry(fileId)) {
    note = EntryStore.inStore(fileId) ? (EntryStore.getNote(fileId) as Note) : null;
  } else if (APIUtil.isEntryTemplate(fileId)) {
    note = TemplateStore.inStore(fileId) ? TemplateStore.getNote(fileId) : null;
  } else if (APIUtil.isDocument(fileId)) {
    note = documentStore.inStore(fileId) ? documentStore.getNote(fileId) : null;
  } else if (APIUtil.isSubtemplate(fileId)) {
    note = SubtemplateStore.inStore(fileId) ? SubtemplateStore.getNote() : null;
  }
  if (note == null) {
    return fallbackNote;
  }
  return note;
}

export default function getStructuredTableMappingContextMenuOptions(
  selectionRange: TableConstants.SelectionRange | null,
  fileId: string,
  isConfig: boolean | undefined,
  note: Note,
  structuredTable: UnknownStructuredTable,
  tableIsLocked: boolean,
  structuredTableMappingsState: StructuredTableMappingsState,
  setStructuredTableMappingsState: (newState: StructuredTableMappingsState) => void
): Array<ClickableContextMenuOption> {
  const columnIndex = selectionRange?.minIndex;
  if (columnIndex == null || !allowTableMappingInDocument(fileId)) {
    return [];
  }
  const selectedColumn = structuredTable.columns[columnIndex];
  const selectedColumnUuid = selectedColumn.provenanceUuid;
  const structuredTableAdapter = getStructuredTableAdapterFromTable(structuredTable);
  const selectedColumnDataType = guessColumnDataType(
    structuredTableAdapter.getColumnDefinitions(structuredTable)[columnIndex]
  );
  const existingMappingsForTable =
    structuredTableMappingsState.mappingsByDestinationTableUuid[structuredTable.provenanceUuid];
  const existingMappingsForColumn = StructuredTableMappingsStateManager.getMappingsForDestinationColumn(
    structuredTableMappingsState,
    structuredTable.provenanceUuid,
    selectedColumnUuid
  );

  const tableHasMappings = Boolean(existingMappingsForTable?.length);
  const columnHasMappings = Boolean(existingMappingsForColumn?.length);
  const tableHasOtherColumnMapping = tableHasMappings && !columnHasMappings;

  const options: Array<ClickableContextMenuOption> = [];

  // Allow configuring (adding/editing) table mappings from destination columns
  if (
    structuredTableAdapter.allowAsTableMappingDestination(structuredTable) &&
    selectedColumnDataType != null
  ) {
    const disabledTooltip = getAddEditDisabledTooltip({selectedColumn, tableHasOtherColumnMapping});
    options.push({
      id: ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
      name: columnHasMappings ? EDIT_TABLE_MAPPING_NAME : ADD_TABLE_MAPPING_NAME,
      displayGroup: ContextMenuDisplayGroup.MIDDLE,
      hide: tableIsLocked,
      click: () => {
        // Rather than passing the note down, we have to make sure to refind the correct note here,
        // otherwise Backbone might have an out of date note for the context menu!
        const currentNote = getNoteFromStore(fileId, note);

        const sessionId = analytics.makeAnalyticsEventId();
        trackStructuredTableMappingConfigModalEvent(
          sessionId,
          CommonAnalyticsActions.OPENED_MODAL,
          fileId,
          structuredTable,
          {isConfig}
        );
        const structuredTables = getAllStructuredTablesInNote(currentNote);
        legacyRunModal(StructuredTableMappingConfigModal, {
          structuredTables,
          structuredTableMappingsState,
          destinationTable: structuredTable,
          destinationColumn: selectedColumn,
          destinationColumnDataType: selectedColumnDataType,
          onRequestSubmit: (newStructuredTableMappingsState: StructuredTableMappingsState) => {
            setStructuredTableMappingsState(newStructuredTableMappingsState);
            trackUpdateStructuredTableMappingsEvent(
              ActionMethod.STRUCTURED_TABLE_MAPPING_CONFIG_SUBMIT,
              fileId,
              false,
              newStructuredTableMappingsState.structuredTableMappings,
              structuredTables,
              structuredTable
            );
          },
          analyticsParams: {
            sessionId,
            fileId,
          },
        });
      },
      disabled: Boolean(disabledTooltip),
      tooltip: disabledTooltip,
      dataTestAttrs: {component: 'ContextMenu', element: 'option', key: 'addTableMapping'},
    });
  }

  // Allow removing table mappings from destination columns
  if (columnHasMappings) {
    const disabledTooltip = getRemoveDisabledTooltip({selectedColumn});
    options.push({
      id: REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
      name: REMOVE_TABLE_MAPPING_NAME,
      displayGroup: ContextMenuDisplayGroup.MIDDLE,
      hide: tableIsLocked,
      click: () => {
        const updatedTableMappingsState = StructuredTableMappingsStateManager.removeDestinationColumnMappings(
          structuredTableMappingsState,
          structuredTable.provenanceUuid,
          selectedColumnUuid
        );
        setStructuredTableMappingsState(updatedTableMappingsState);
        // Rather than passing the note down, we have to make sure to refind the correct note here,
        // otherwise Backbone might have an out of date note for the context menu!
        const currentNote = getNoteFromStore(fileId, note);
        trackUpdateStructuredTableMappingsEvent(
          ActionMethod.STRUCTURED_TABLE_COLUMN_MENU,
          fileId,
          false,
          updatedTableMappingsState.structuredTableMappings,
          getAllStructuredTablesInNote(currentNote),
          structuredTable
        );
      },
      disabled: Boolean(disabledTooltip),
      tooltip: disabledTooltip,
      dataTestAttrs: {component: 'ContextMenu', element: 'option', key: 'removeTableMapping'},
    });
  }

  return options;
}

function getAddEditDisabledTooltip({
  selectedColumn,
  tableHasOtherColumnMapping,
}: {
  selectedColumn: StructuredTableColumn;
  tableHasOtherColumnMapping: boolean;
}): string | undefined {
  if (isReadOnly(selectedColumn)) {
    return TOOLTIP_DISABLED_READ_ONLY;
  }
  if (selectedColumn.isArchived) {
    return TOOLTIP_DISABLED_ARCHIVED;
  }
  if (tableHasOtherColumnMapping) {
    return TOOLTIP_DISABLED_OTHER_COLUMN_MAPPED;
  }
  return undefined;
}

function getRemoveDisabledTooltip({
  selectedColumn,
}: {
  selectedColumn: StructuredTableColumn;
}): string | undefined {
  if (isReadOnly(selectedColumn)) {
    return TOOLTIP_DISABLED_READ_ONLY;
  }
  if (selectedColumn.isArchived) {
    return TOOLTIP_DISABLED_ARCHIVED;
  }
  return undefined;
}
